import React from 'react';
import { Skeleton, Flex, Grid, Box, Stack, Tabs, Table } from '@mantine/core';
import styled from 'styled-components';
import { Section } from '../Container/Section';
import { SectionTitleWithExp } from '../customTypo/SectionTitleWithExp';
import { CardContainerRetro } from '../Container/CardContainerRetro';
import colorsCustom from '../../designTokens/colorsCustom';
import colors from '../../designTokens/colors.esm';

/**
 * Supported skeleton layout variants.
 * Each value reproduces a previously standalone skeleton component.
 */
export const SECTION_SKELETON_VARIANTS = Object.freeze({
  cardGrid: 'cardGrid',
  grid: 'grid',
  chart: 'chart',
  table: 'table',
  performanceSummary: 'performanceSummary',
});

// --- performanceSummary variant styled helpers (ported verbatim) ---
const Col = styled(Grid.Col)`
  padding: 0 16px;
`;

const Block = styled(Flex)`
  border-right: ${(props) =>
    `solid ${props.$isEnd ? '0' : '0.5px'} ${colorsCustom.ui.dark.subtle}`};
  margin-bottom: ${(props) => (props.$isMobile ? '16px' : '0px')};
`;

/**
 * cardGrid variant — list of post cards with filter header.
 * Renders one column list on mobile (tabs) and three columns on desktop.
 *
 * @param {boolean} isMobile - viewport flag [Required]
 * @param {number} cardCount - number of card placeholders per column [Required]
 * @param {number} columns - number of desktop columns [Required]
 */
const renderCardListSkeleton = (cardCount) => (
  <Flex direction="column" gap={16}>
    {/* 필터 영역 */}
    <Flex direction="column" gap={8}>
      <Flex justify="space-between" align="center">
        <Skeleton height={20} width={150} />
        <Skeleton height={32} width={120} />
      </Flex>
      <Skeleton height={14} width="80%" />
    </Flex>

    {/* 카드 리스트 */}
    <Stack gap={8}>
      {Array.from({ length: cardCount }, (_, index) => (
        <Flex
          key={index}
          direction="column"
          gap={8}
          p={12}
          style={{
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
          }}
        >
          {/* 카드 헤더 */}
          <Flex justify="space-between" align="center">
            <Skeleton height={12} width={80} />
            <Skeleton height={20} width={20} circle />
          </Flex>

          {/* 카드 본문 */}
          <Stack gap={4}>
            <Skeleton height={16} width="100%" />
            <Skeleton height={16} width="80%" />
            <Skeleton height={16} width="60%" />
          </Stack>

          {/* 메트릭 */}
          <Flex justify="space-between" align="center">
            <Flex gap={16}>
              <Skeleton height={14} width={50} />
              <Skeleton height={14} width={40} />
              <Skeleton height={14} width={40} />
              <Skeleton height={14} width={50} />
            </Flex>
          </Flex>
        </Flex>
      ))}
    </Stack>
  </Flex>
);

const CardGridVariant = ({ isMobile, cardCount, columns }) => (
  <CardContainerRetro
    p={isMobile ? 16 : 24}
    bg={colorsCustom.paper}
    weight="bold"
    rd={16}
  >
    <Flex direction="column" gap={24}>
      {/* 상단 필터 컨트롤 */}
      <Flex direction="column" gap={16}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
          <Skeleton height={32} width={120} />
          {isMobile && (
            <Flex gap={8}>
              <Skeleton height={32} width={80} />
              <Skeleton height={32} width={80} />
            </Flex>
          )}
        </Flex>
      </Flex>

      {isMobile ? (
        <Tabs defaultValue="views">
          <Tabs.List>
            <Tabs.Tab value="views">
              <Skeleton height={16} width={60} />
            </Tabs.Tab>
            <Tabs.Tab value="rate">
              <Skeleton height={16} width={80} />
            </Tabs.Tab>
            <Tabs.Tab value="amount">
              <Skeleton height={16} width={70} />
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="views" pt="md">
            {renderCardListSkeleton(cardCount)}
          </Tabs.Panel>
        </Tabs>
      ) : (
        <Grid>
          {Array.from({ length: columns }, (_, index) => (
            <Grid.Col span={12 / columns} key={index}>
              {renderCardListSkeleton(cardCount)}
            </Grid.Col>
          ))}
        </Grid>
      )}
    </Flex>
  </CardContainerRetro>
);

/**
 * grid variant — avatar grid (top friends list).
 *
 * @param {boolean} isMobile - viewport flag [Required]
 * @param {number} itemCount - number of avatar cells [Required]
 */
const GridVariant = ({ isMobile, itemCount }) => {
  // 다양한 사용자명 길이를 시뮬레이션하기 위한 고정 배열
  const userNameWidths = [60, 80, 70, 90, 65, 85, 75, 95];

  return (
    <CardContainerRetro
      p={isMobile ? '16px' : '48px 32px 48px 24px'}
      bg={colorsCustom.paper}
      weight="bold"
      rd={16}
      style={{ position: 'relative' }}
    >
      <Grid w="100%" wrap="wrap" gutter="48px">
        {Array.from({ length: itemCount }, (_, index) => (
          <Grid.Col span={isMobile ? 6 : 3} key={index}>
            <Flex
              w="100%"
              direction="column"
              align="center"
              justify="center"
              gap="8px"
              style={{ cursor: 'pointer' }}
            >
              {/* 프로필 이미지 Skeleton */}
              <Skeleton height={50} width={50} circle />

              {/* 사용자명 Skeleton - 고정 배열로 다양성 제공 */}
              <Skeleton
                height={isMobile ? 16 : 20}
                width={userNameWidths[index % userNameWidths.length]}
              />

              {/* 댓글 수 Skeleton */}
              <Flex align="center" gap={4}>
                <Skeleton height={18} width={20} />
                <Skeleton height={18} width={60} />
              </Flex>
            </Flex>
          </Grid.Col>
        ))}
      </Grid>
    </CardContainerRetro>
  );
};

/**
 * chart variant — tabbed chart panel with controls.
 *
 * @param {boolean} isMobile - viewport flag [Required]
 * @param {number} height - chart placeholder height [Required]
 */
const ChartVariant = ({ isMobile, height }) => (
  <CardContainerRetro
    p={isMobile ? '16px' : '24px 32px 48px 24px'}
    bg={colorsCustom.paper}
    weight="bold"
    rd={16}
    style={{ position: 'relative' }}
  >
    <Tabs defaultValue="profile" w="100%">
      <Tabs.List>
        <Tabs.Tab value="profile" fz={isMobile ? 'md' : 'lg'}>
          <Skeleton height={16} width={100} />
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="profile" pt="md">
        <Flex direction="column" gap={16} w="100%">
          {/* 설명 텍스트 Skeleton */}
          <Skeleton height={isMobile ? 14 : 16} width="80%" />

          {/* 컨트롤 영역 Skeleton */}
          <Flex w="100%" align="center" gap={16}>
            <Skeleton height={isMobile ? 32 : 40} width={120} />
            <Skeleton height={24} width={1} />
            <Flex align="center" gap={8}>
              <Skeleton height={isMobile ? 24 : 32} width={isMobile ? 24 : 32} />
              <Skeleton height={isMobile ? 16 : 20} width={80} />
              <Skeleton height={isMobile ? 24 : 32} width={isMobile ? 24 : 32} />
            </Flex>
          </Flex>

          {/* 차트 영역 Skeleton */}
          <Flex w="100%">
            <Skeleton
              height={height ?? (isMobile ? 240 : 320)}
              width="100%"
              radius="md"
            />
          </Flex>
        </Flex>
      </Tabs.Panel>
    </Tabs>
  </CardContainerRetro>
);

/**
 * table variant — two-column table + box plot rows.
 *
 * @param {boolean} isMobile - viewport flag [Required]
 * @param {number} rowCount - number of table body rows [Required]
 */
const TableVariant = ({ isMobile, rowCount }) => (
  <CardContainerRetro
    p={isMobile ? '16 0' : '24 0'}
    w="100%"
    bg={colorsCustom.paper}
    weight="bold"
    rd={16}
  >
    <Flex direction="column" gap={isMobile ? 8 : 16} w="100%">
      <Grid gutter={0}>
        <Grid.Col
          span={isMobile ? 12 : 6}
          style={{
            borderRight: !isMobile ? `0.5px solid ${colors.gray[3]}` : 'none',
            borderBottom: isMobile ? `0.5px solid ${colors.gray[3]}` : 'none',
          }}
        >
          <Flex
            p={isMobile ? '16' : '24'}
            direction="column"
            justify="center"
            gap={16}
            style={{
              height: '100%',
              overflow: 'auto',
            }}
          >
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <Skeleton height={isMobile ? 14 : 18} width={50} />
                  </Table.Th>
                  <Table.Th>
                    <Skeleton height={isMobile ? 14 : 18} width={60} />
                  </Table.Th>
                  <Table.Th>
                    <Skeleton height={isMobile ? 14 : 18} width={50} />
                  </Table.Th>
                  <Table.Th>
                    <Skeleton height={isMobile ? 14 : 18} width={60} />
                  </Table.Th>
                  <Table.Th>
                    <Skeleton height={isMobile ? 14 : 18} width={70} />
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {Array.from({ length: rowCount }, (_, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>
                      <Skeleton height={isMobile ? 14 : 18} width={30} />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={isMobile ? 14 : 18} width={50} />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={isMobile ? 14 : 18} width={40} />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={isMobile ? 14 : 18} width={40} />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={isMobile ? 14 : 18} width={45} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Flex>
        </Grid.Col>
        <Grid.Col span={isMobile ? 12 : 6}>
          <Flex
            p={isMobile ? '16' : '24'}
            direction="column"
            justify="center"
            gap={16}
            style={{
              height: '100%',
            }}
          >
            {/* Box Plot Skeleton */}
            {['views', 'likes', 'replies', 'reposts'].map((metric) => (
              <Flex key={metric} direction="column" gap={8} w="100%">
                <Flex justify="space-between" align="center">
                  <Skeleton height={isMobile ? 14 : 16} width={80} />
                  <Skeleton height={isMobile ? 14 : 16} width={60} />
                </Flex>
                <Flex align="center" gap={8}>
                  <Skeleton height={isMobile ? 14 : 16} width={30} />
                  <Flex
                    style={{
                      width: '100%',
                      height: 16,
                      position: 'relative',
                    }}
                  >
                    <Skeleton height={16} width="100%" radius="xs" />
                  </Flex>
                  <Skeleton height={isMobile ? 14 : 16} width={30} />
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Grid.Col>
      </Grid>
    </Flex>
  </CardContainerRetro>
);

/**
 * performanceSummary variant — profile block + four metric cards.
 *
 * @param {boolean} isMobile - viewport flag [Required]
 */
const PerformanceSummaryVariant = ({ isMobile }) => (
  <CardContainerRetro bg={colorsCustom.paper} w="100%" weight="bold" rd={16}>
    <Flex
      w="100%"
      p={isMobile ? '16px 0px 0px 0px' : '24px 8px'}
      gap={24}
      direction="column"
    >
      <Grid
        direction="row"
        w="100%"
        h={isMobile ? 'auto' : 109}
        gutter={0}
        p="0px"
        style={{
          display: 'flex',
          alignItems: 'stretch',
          minHeight: '100%',
        }}
      >
        {/* 프로필 섹션 */}
        <Col span={isMobile ? 12 : 4}>
          <Block gap={24} $isEnd={isMobile} $isMobile={isMobile}>
            <Box
              w="fit-content"
              h="fit-content"
              style={{
                borderRadius: '50%',
                padding: 8,
                border: `4px solid ${colorsCustom.ui.dark.bold}`,
              }}
            >
              <Skeleton
                height={isMobile ? 56 : 72}
                width={isMobile ? 56 : 72}
                circle
              />
            </Box>
            <Stack gap={20}>
              <Stack gap={8}>
                <Skeleton height={isMobile ? 16 : 20} width="60%" />
                <Skeleton height={isMobile ? 12 : 16} width="50%" />
                <Flex gap={4} align="center">
                  <Skeleton height={16} width={16} />
                  <Skeleton height={isMobile ? 12 : 16} width="40%" />
                </Flex>
              </Stack>
            </Stack>
          </Block>
        </Col>

        {/* 지표 카드들 */}
        <Col span={isMobile ? 6 : 'auto'}>
          <Block $isMobile={isMobile}>
            <Flex direction="column" align="center" gap={8}>
              <Skeleton height={20} width={80} />
              <Skeleton height={32} width="100%" />
            </Flex>
          </Block>
        </Col>
        <Col span={isMobile ? 6 : 'auto'}>
          <Block $isMobile={isMobile} $isEnd={isMobile}>
            <Flex direction="column" align="center" gap={8}>
              <Skeleton height={20} width={80} />
              <Skeleton height={32} width="100%" />
            </Flex>
          </Block>
        </Col>
        <Col span={isMobile ? 6 : 'auto'}>
          <Block $isMobile={isMobile}>
            <Flex direction="column" align="center" gap={8}>
              <Skeleton height={20} width={80} />
              <Skeleton height={32} width="100%" />
            </Flex>
          </Block>
        </Col>
        <Col span={isMobile ? 6 : 'auto'}>
          <Block $isEnd={true} $isMobile={isMobile}>
            <Flex direction="column" align="center" gap={8}>
              <Skeleton height={20} width={80} />
              <Skeleton height={32} width="100%" />
            </Flex>
          </Block>
        </Col>
      </Grid>
    </Flex>
  </CardContainerRetro>
);

/**
 * SectionSkeleton — unified loading skeleton for dashboard/analytic sections.
 *
 * Replaces the previous CardGridSkeleton / GridSkeleton / ChartSkeleton /
 * TableSkeleton / PerformanceSummarySkeleton family. Pick a layout with the
 * `variant` prop; each variant reproduces its original layout faithfully.
 *
 * @param {string} variant - layout to render: 'cardGrid' | 'grid' | 'chart' | 'table' | 'performanceSummary' [Optional, 기본값: 'cardGrid']
 * @param {string} title - section title shown in SectionTitleWithExp [Optional]
 * @param {boolean} isMobile - responsive flag passed by the consuming section [Optional, 기본값: false]
 * @param {number} cardCount - cards per column for 'cardGrid' variant [Optional, 기본값: 5]
 * @param {number} columns - desktop column count for 'cardGrid' variant [Optional, 기본값: 3]
 * @param {number} itemCount - avatar cells for 'grid' variant [Optional, 기본값: 8]
 * @param {number} rowCount - body rows for 'table' variant [Optional, 기본값: 3]
 * @param {number} height - chart placeholder height for 'chart' variant [Optional, 기본값: 240 모바일 / 320 데스크톱]
 *
 * Example usage:
 *   <SectionSkeleton variant="grid" title={t('topActiveFriends')} />
 */
export const SectionSkeleton = ({
  variant = SECTION_SKELETON_VARIANTS.cardGrid,
  title,
  isMobile = false,
  cardCount = 5,
  columns = 3,
  itemCount = 8,
  rowCount = 3,
  height,
}) => {
  const renderVariant = () => {
    switch (variant) {
      case SECTION_SKELETON_VARIANTS.grid:
        return <GridVariant isMobile={isMobile} itemCount={itemCount} />;
      case SECTION_SKELETON_VARIANTS.chart:
        return <ChartVariant isMobile={isMobile} height={height} />;
      case SECTION_SKELETON_VARIANTS.table:
        return <TableVariant isMobile={isMobile} rowCount={rowCount} />;
      case SECTION_SKELETON_VARIANTS.performanceSummary:
        return <PerformanceSummaryVariant isMobile={isMobile} />;
      case SECTION_SKELETON_VARIANTS.cardGrid:
      default:
        return (
          <CardGridVariant
            isMobile={isMobile}
            cardCount={cardCount}
            columns={columns}
          />
        );
    }
  };

  return (
    <Section>
      <SectionTitleWithExp title={title} isMobile={isMobile} />
      {renderVariant()}
    </Section>
  );
};

export default SectionSkeleton;
